import { useMemo, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import {
    parseNoteForIbic,
    reconstructNoteText,
    updateSegmentValue,
    ParsedSegment
} from "../../utils/ibicLineParser";
import { IbicInlineSelector } from "../IbicInlineSelector";
import styles from "./InteractiveNoteContent.module.css";

interface InteractiveNoteContentProps {
    content: string;
    onContentChange: (newContent: string) => void;
    isEditable?: boolean;
}

export const InteractiveNoteContent = ({
    content,
    onContentChange,
    isEditable = true
}: InteractiveNoteContentProps) => {
    // Parse the content to identify IBIC lines
    const parsedNote = useMemo(() => parseNoteForIbic(content), [content]);

    // Handle IBIC selection change
    const handleIbicChange = useCallback(
        (segmentIndex: number, newValue: string) => {
            const updatedSegments = updateSegmentValue(
                parsedNote.segments,
                segmentIndex,
                newValue
            );
            const newContent = reconstructNoteText(
                updatedSegments,
                parsedNote.detectedLanguage
            );
            onContentChange(newContent);
        },
        [parsedNote, onContentChange]
    );

    // If no IBIC lines found, render as plain markdown
    if (!parsedNote.hasIbic) {
        return (
            <div className={styles.content}>
                <ReactMarkdown>{content}</ReactMarkdown>
            </div>
        );
    }

    // Render segments
    return (
        <div className={styles.content}>
            {parsedNote.segments.map((segment, index) => (
                <SegmentRenderer
                    key={`${segment.type}-${index}`}
                    segment={segment}
                    index={index}
                    language={parsedNote.detectedLanguage}
                    isEditable={isEditable}
                    onIbicChange={handleIbicChange}
                />
            ))}
        </div>
    );
};

interface SegmentRendererProps {
    segment: ParsedSegment;
    index: number;
    language: "en" | "sv";
    isEditable: boolean;
    onIbicChange: (index: number, newValue: string) => void;
}

const SegmentRenderer = ({
    segment,
    index,
    language,
    isEditable,
    onIbicChange
}: SegmentRendererProps) => {
    if (segment.type === "text") {
        return (
            <div className={styles.textSegment}>
                <ReactMarkdown>{segment.content}</ReactMarkdown>
            </div>
        );
    }

    if (segment.type === "ibic-category") {
        return (
            <div className={styles.ibicSegment}>
                <IbicInlineSelector
                    label={segment.label || "IBIC"}
                    value={segment.value || ""}
                    onChange={(newValue) => onIbicChange(index, newValue)}
                    type="category"
                    language={language}
                    disabled={!isEditable}
                />
            </div>
        );
    }

    if (segment.type === "ibic-code") {
        return (
            <div className={styles.ibicSegment}>
                <IbicInlineSelector
                    label={segment.label || (language === "sv" ? "IBIC-kod" : "IBIC Code")}
                    value={segment.value || ""}
                    onChange={(newValue) => onIbicChange(index, newValue)}
                    type="code"
                    language={language}
                    disabled={!isEditable}
                />
            </div>
        );
    }

    return null;
};

export default InteractiveNoteContent;
